/**
 * Weekly OTIF Chart Component
 *
 * Line chart showing OTIF percentage and line counts per receipt week. The
 * backend rolls GRN receipts up by Monday-start week (a day-by-day line
 * over a month was 31 jittery points nobody reviews at that grain).
 */
import { forwardRef } from 'react'
import { Card, CardContent } from '../ui/card'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { type PerformanceScorecardDailyOTIF } from '../../lib/api'

interface DailyOTIFChartProps {
  data: PerformanceScorecardDailyOTIF[]
}

export const PerformanceDailyOTIFChart = forwardRef<HTMLDivElement, DailyOTIFChartProps>(
  function PerformanceDailyOTIFChart({ data }, ref) {
    if (!data || data.length === 0) return null
    // Target comes from the tenant's supplier-performance settings (same on every row)
    const target = data[0]?.target ?? 80

    return (
      <div ref={ref} className="bg-white p-4">
        <Card className="shadow-sm">
          <CardContent className="pt-4 pb-6">
            <h3 className="text-lg font-semibold mb-4 text-center">Weekly OTIF</h3>
            <div className="w-full h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 10, right: 50, left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={0} height={30} />
                  <YAxis
                    yAxisId="left"
                    domain={[0, 100]}
                    tickFormatter={(v) => `${v}%`}
                    label={{ value: 'OTIF %', angle: -90, position: 'insideLeft' }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    allowDecimals={false}
                    label={{ value: 'Lines', angle: 90, position: 'insideRight' }}
                  />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (value === null || value === undefined) return ['N/A', name]
                      if (name === 'OTIF' || name === 'Target') return [`${value}%`, name]
                      return [value, name]
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    align="center"
                    verticalAlign="bottom"
                    wrapperStyle={{ paddingTop: 10, display: 'flex', justifyContent: 'center' }}
                  />
                  <ReferenceLine
                    yAxisId="left"
                    y={target}
                    stroke="#ef4444"
                    strokeDasharray="5 5"
                    label={{ value: `Target ${target}%`, fill: '#ef4444', fontSize: 10 }}
                  />
                  <Line
                    yAxisId="left"
                    type="linear"
                    dataKey="otif_percent"
                    name="OTIF"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                  />
                  <Line
                    yAxisId="right"
                    type="linear"
                    dataKey="on_time"
                    name="OTIF PO Lines"
                    stroke="#22c55e"
                    strokeWidth={1}
                    dot={{ r: 2 }}
                    connectNulls={false}
                  />
                  <Line
                    yAxisId="right"
                    type="linear"
                    dataKey="failed"
                    name="Failed OTIF"
                    stroke="#ef4444"
                    strokeWidth={1}
                    dot={{ r: 2 }}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }
)
